import React, { useState, useEffect, SetStateAction } from 'react';
import { UseAutoFeedbackResponse } from './hooks/useAutoFeedback';
import { Attachment, AutoFeedbackItemStatus } from '@scale/shared/types/chat';
import { Button, ProgressStepper, SelectMenu, Skeleton, Spinner, Typography } from '@scale/scaleui';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { twMerge } from 'tailwind-merge';
import AutoFeedbackStatus from './components/AutoFeedbackStatus';
import AutoFeedbackBadge, {
  priorityVariantMapping,
  priorityTextMapping,
} from './components/AutoFeedbackBadge';
import { getAutoFeedbackStatus } from 'react-scale/components/scalers/tasks/ThreadedInteraction/AutoFeedback/helpers';
import useSimulatedFeedback from 'react-scale/components/scalers/tasks/ThreadedInteraction/AutoFeedback/hooks/useSimulatedProgress';
import { EvaluatorTemplateJSON } from '@scale/shared/types/corp/chatCompanion';
import { IChangedMap } from 'react-scale/corp/views/CorpProjectGroupManager/views/ProjectConfig/ChatCompanion';

interface AutoFeedbackProps {
  autoFeedbackHook?: UseAutoFeedbackResponse | null;
  inputContent: string;
  attachments?: Attachment[];
  readOnly?: boolean;
  template?: EvaluatorTemplateJSON;
  setIsChangedMap?: (value: SetStateAction<IChangedMap>) => void;
}

// eslint-disable-next-line
const AutoFeedback: React.FC<AutoFeedbackProps> = ({
  autoFeedbackHook,
  inputContent,
  attachments,
  readOnly,
  template,
  setIsChangedMap,
}) => {
  const [feedbackCategoryFilter, setFeedbackCategoryFilter] = useState<string | 'All'>('All');
  const [feedbackPriorityFilter, setFeedbackPriorityFilter] = useState<number>(-1);
  const feedbackToDisplay = (autoFeedbackHook?.feedback ?? []).filter(
    f => f.status !== AutoFeedbackItemStatus.RESOLVED,
  );
  const { progress, startSimulatedProgress, stopSimulatedProgress } = useSimulatedFeedback({
    maxProgress: 100, // 100 steps
    countdownTimeSecs: 30, // 30 seconds
  });

  const feedbackCategories = _.uniq(
    autoFeedbackHook?.feedback?.map(f => f.category ?? f.evaluatorName) ?? [],
  )
    .map(category => ({
      id: category,
      name: category,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const priorityOptions = Object.entries(priorityTextMapping).map(([id, name]) => ({
    id: parseInt(id),
    name,
  }));

  useEffect(() => {
    if (!autoFeedbackHook?.isFetchingFeedback) {
      stopSimulatedProgress();
    }
  }, [autoFeedbackHook?.isFetchingFeedback, stopSimulatedProgress]);

  return (
    <div className="p-3">
      <AutoFeedbackStatus
        className="mb-5"
        state={getAutoFeedbackStatus(autoFeedbackHook ?? undefined)}
      />
      {progress < 100 && (
        <ProgressStepper className="mb-5" size="sm" steps={100} stepsCompleted={progress} />
      )}
      <div hidden={!autoFeedbackHook?.feedback} className="flex flex-row justify-between mt-2">
        {autoFeedbackHook && (
          <div className="flex mt-0.5">
            <Button
              className="text-sm"
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                startSimulatedProgress();
                autoFeedbackHook.handleFetchFeedback({
                  inputText: inputContent,
                  attachments,
                  template,
                });
                if (setIsChangedMap && template?.stableId) {
                  setIsChangedMap(prev => ({
                    ...prev,
                    [template.stableId!]: {
                      isChanged: prev[template.stableId!].isChanged,
                      previewModeRan: true,
                    },
                  }));
                }
              }}
              size="sm"
              variant="primary"
              disabled={autoFeedbackHook.isFetchingFeedback || readOnly}
            >
              {autoFeedbackHook.isFetchingFeedback && !autoFeedbackHook?.feedback?.length ? (
                <Spinner size="xs" variant="primary" />
              ) : (
                <FontAwesomeIcon icon={'play'}></FontAwesomeIcon>
              )}

              <span className="pl-2">
                {autoFeedbackHook.isFetchingFeedback ? 'Evaluating...' : 'Get Feedback'}
              </span>
            </Button>
          </div>
        )}
        {!!autoFeedbackHook?.feedback?.length && (
          <div className="flex flex-row justify-end">
            <Button
              className={twMerge(
                'mr-3 underline text-sm mt-0.5',
                feedbackPriorityFilter !== -1 || feedbackCategoryFilter !== 'All' ? '' : 'hidden',
              )}
              size="sm"
              variant="transparent"
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                setFeedbackPriorityFilter(-1);
                setFeedbackCategoryFilter('All');
              }}
              disabled={autoFeedbackHook?.isFetchingFeedback}
            >
              Clear Filters
            </Button>

            <SelectMenu<number>
              menuDirection="bottom"
              selectedOptionId={feedbackPriorityFilter ?? -1}
              setSelected={(option: number) => setFeedbackPriorityFilter(option)}
              options={[{ id: -1, name: 'Filter by Priority' }, ...priorityOptions]}
              floatStrategy="fixed"
              disabled={autoFeedbackHook?.isFetchingFeedback}
            />

            <SelectMenu<string | 'All'>
              className="ml-3"
              menuDirection="bottom"
              selectedOptionId={feedbackCategoryFilter ?? 'All'}
              setSelected={(option: string | 'All') => setFeedbackCategoryFilter(option)}
              options={[{ id: 'All', name: 'Filter by Category' }, ...feedbackCategories]}
              floatStrategy="fixed"
              disabled={autoFeedbackHook?.isFetchingFeedback}
            />
          </div>
        )}
      </div>

      <div
        className={twMerge(
          'overflow-auto max-h-[600px]',
          feedbackToDisplay?.length ? 'mt-5' : 'mb-0',
        )}
      >
        {feedbackToDisplay
          ?.filter(feedbackItem => {
            const { priority, evaluatorName, category } = feedbackItem;
            if (feedbackPriorityFilter !== -1 && priority !== feedbackPriorityFilter) {
              return false;
            }
            if (
              feedbackCategoryFilter !== 'All' &&
              (category ?? evaluatorName) !== feedbackCategoryFilter
            ) {
              return false;
            }
            return true;
          })
          .map((feedbackItem, index) => {
            const { message, priority, evaluatorName, category } = feedbackItem;

            return (
              <div className="text-sm" key={index}>
                <div>
                  <div>
                    <div>
                      <Typography.Paragraph2>
                        <div className="font-semibold">Feedback Item #{index + 1}</div>
                      </Typography.Paragraph2>
                      <div className="flex text-neutral-700 mt-1" style={{ gap: 5 }}>
                        {priority !== undefined ? (
                          <AutoFeedbackBadge variant={priorityVariantMapping[priority]}>
                            {priorityTextMapping[priority]}
                          </AutoFeedbackBadge>
                        ) : null}
                        <div hidden={!(category ?? evaluatorName)}>
                          <AutoFeedbackBadge variant={'info'}>
                            {category ?? evaluatorName}
                          </AutoFeedbackBadge>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-row items-center justify-between w-full">
                    <div
                      className={twMerge(
                        'w-8/12',
                        index + 1 === feedbackToDisplay?.length ? 'pt-3' : 'py-3',
                      )}
                    >
                      <Typography.Paragraph2>
                        {message || (feedbackItem as unknown as React.ReactNode)}
                      </Typography.Paragraph2>
                    </div>

                    <div className="flex w-4/12 ml-3 gap-3 justify-end pr-2">
                      <Button
                        disabled={autoFeedbackHook?.isFetchingFeedback || readOnly}
                        onClick={e => {
                          e.stopPropagation();
                          e.preventDefault();
                          autoFeedbackHook?.setFeedbackState(
                            feedbackItem.id,
                            AutoFeedbackItemStatus.FIXED,
                          );
                        }}
                        variant="white"
                        className={
                          feedbackItem.status === AutoFeedbackItemStatus.FIXED
                            ? 'inline-flex items-center rounded-md border-0 font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all h-fit px-2.5 py-1.5 text-xs text-success-800 bg-success-100 hover:bg-success-200 focus:ring-success-100'
                            : 'inline-flex items-center rounded-md border-0 font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all h-fit px-2.5 py-1.5 text-xs'
                        }
                      >
                        Agree & Fixed
                      </Button>
                      <Button
                        disabled={autoFeedbackHook?.isFetchingFeedback || readOnly}
                        onClick={e => {
                          e.stopPropagation();
                          e.preventDefault();
                          autoFeedbackHook?.setFeedbackState(
                            feedbackItem.id,
                            AutoFeedbackItemStatus.IGNORED,
                          );
                        }}
                        variant="white"
                        className={
                          feedbackItem.status === AutoFeedbackItemStatus.IGNORED
                            ? 'inline-flex items-center rounded-md border-0 font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all h-fit px-2.5 py-1.5 text-xs text-danger-600 bg-danger-100 hover:bg-danger-200 focus:ring-danger-100'
                            : 'inline-flex items-center rounded-md border-0 font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all h-fit px-2.5 py-1.5 text-xs'
                        }
                      >
                        Disagree
                      </Button>
                    </div>
                  </div>
                </div>
                <hr className="my-3 border-neutral-200" />
              </div>
            );
          })}
        {autoFeedbackHook?.isFetchingFeedback && (
          <div>
            {!autoFeedbackHook?.feedback?.length && <hr className="my-4 border-neutral-200" />}
            <Skeleton className="mt-4" />
            <Skeleton className="mt-4 w-full" />
            <hr className="my-4 border-neutral-200" />
          </div>
        )}
      </div>
    </div>
  );
};

// Export the component
export default AutoFeedback;
